/**
 * AnamnesisForm Component
 * Form for occupational anamnesis
 *
 * @version 2025.1.0
 */

import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Typography,
  Divider,
} from '@mui/material';

import {
  UpdateMedicalRecordData,
  SmokingStatus,
  AlcoholUse,
  getSmokingStatusLabel,
  getAlcoholUseLabel,
} from '../../../types/medicalRecord';

interface AnamnesisFormProps {
  data: UpdateMedicalRecordData;
  onChange: (data: UpdateMedicalRecordData) => void;
  disabled?: boolean;
}

export const AnamnesisForm: React.FC<AnamnesisFormProps> = ({
  data,
  onChange,
  disabled = false,
}) => {
  const handleChange = (field: keyof UpdateMedicalRecordData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onChange({ ...data, [field]: event.target.value ?? null });
  };

  const handleSwitchChange = (field: keyof UpdateMedicalRecordData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({ ...data, [field]: event.target.checked });
  };

  return (
    <Box>
      {/* Queixa Principal */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Queixa Principal
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={2}
        placeholder="Descreva a queixa principal do paciente..."
        value={data.chiefComplaint ?? ''}
        onChange={handleChange('chiefComplaint')}
        disabled={disabled}
        sx={{ mb: 3 }}
      />
      <Divider sx={{ my: 2 }} />
      {/* Histórico Ocupacional */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Histórico Ocupacional
      </Typography>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Atividade Atual"
            placeholder="Descreva a atividade ocupacional atual..."
            value={data.currentJobDescription ?? ''}
            onChange={handleChange('currentJobDescription')}
            disabled={disabled}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Histórico Ocupacional"
            placeholder="Descreva o histórico ocupacional..."
            value={data.occupationalHistory ?? ''}
            onChange={handleChange('occupationalHistory')}
            disabled={disabled}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Empregos Anteriores"
            placeholder="Liste empregos anteriores relevantes..."
            value={data.previousJobs ?? ''}
            onChange={handleChange('previousJobs')}
            disabled={disabled}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Histórico de Acidentes de Trabalho"
            placeholder="Descreva acidentes de trabalho anteriores..."
            value={data.workAccidentHistory ?? ''}
            onChange={handleChange('workAccidentHistory')}
            disabled={disabled}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      {/* Histórico Médico */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Histórico Médico
      </Typography>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Histórico Médico Pessoal"
            placeholder="Doenças pré-existentes, cirurgias, internações..."
            value={data.medicalHistory ?? ''}
            onChange={handleChange('medicalHistory')}
            disabled={disabled}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Histórico Familiar"
            placeholder="Doenças familiares relevantes..."
            value={data.familyHistory ?? ''}
            onChange={handleChange('familyHistory')}
            disabled={disabled}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Medicamentos em Uso"
            placeholder="Liste medicamentos em uso contínuo..."
            value={data.currentMedications ?? ''}
            onChange={handleChange('currentMedications')}
            disabled={disabled}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Alergias"
            placeholder="Liste alergias conhecidas..."
            value={data.allergies ?? ''}
            onChange={handleChange('allergies')}
            disabled={disabled}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      {/* Hábitos */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Hábitos de Vida
      </Typography>
      <Grid container spacing={3}>
        {/* Tabagismo */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl component="fieldset" disabled={disabled}>
            <FormLabel component="legend">Tabagismo</FormLabel>
            <RadioGroup
              value={data.smokingStatus ?? ''}
              onChange={(e) =>
                onChange({ ...data, smokingStatus: e.target.value as SmokingStatus })
              }
            >
              {Object.values(SmokingStatus).map((status) => (
                <FormControlLabel
                  key={status}
                  value={status}
                  control={<Radio size="small" />}
                  label={getSmokingStatusLabel(status)}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {(data.smokingStatus === SmokingStatus.CURRENT ||
            data.smokingStatus === SmokingStatus.FORMER) && (
            <TextField
              fullWidth
              size="small"
              label="Detalhes"
              placeholder="Quantidade, tempo..."
              value={data.smokingDetails ?? ''}
              onChange={handleChange('smokingDetails')}
              disabled={disabled}
              sx={{ mt: 1 }}
            />
          )}
        </Grid>

        {/* Álcool */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl component="fieldset" disabled={disabled}>
            <FormLabel component="legend">Uso de Álcool</FormLabel>
            <RadioGroup
              value={data.alcoholUse ?? ''}
              onChange={(e) => onChange({ ...data, alcoholUse: e.target.value as AlcoholUse })}
            >
              {Object.values(AlcoholUse).map((use) => (
                <FormControlLabel
                  key={use}
                  value={use}
                  control={<Radio size="small" />}
                  label={getAlcoholUseLabel(use)}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {(data.alcoholUse === AlcoholUse.OCCASIONAL ||
            data.alcoholUse === AlcoholUse.REGULAR) && (
            <TextField
              fullWidth
              size="small"
              label="Detalhes"
              placeholder="Tipo, frequência..."
              value={data.alcoholDetails ?? ''}
              onChange={handleChange('alcoholDetails')}
              disabled={disabled}
              sx={{ mt: 1 }}
            />
          )}
        </Grid>

        {/* Atividade Física */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl component="fieldset" disabled={disabled}>
            <FormLabel component="legend">Atividade Física</FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={data.physicalActivity || false}
                  onChange={handleSwitchChange('physicalActivity')}
                  disabled={disabled}
                />
              }
              label={data.physicalActivity ? 'Pratica' : 'Não pratica'}
            />
          </FormControl>
          {data.physicalActivity && (
            <TextField
              fullWidth
              size="small"
              label="Detalhes"
              placeholder="Tipo, frequência..."
              value={data.physicalActivityDetails ?? ''}
              onChange={handleChange('physicalActivityDetails')}
              disabled={disabled}
              sx={{ mt: 1 }}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnamnesisForm;
